import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'

import { SalesLead, SalesLeadItem } from 'src/app/shared/sales-leads'

/**
 * REST client for sales leads and sales lead line items.
 * Covers the `/SalesLeads` backend route group.
 *
 * A sales lead (`SalesLead`) represents a pre-sales opportunity tracked in
 * `DOCREG`. Each lead can contain multiple line items (`SalesLeadItem`)
 * describing the products or services of interest.
 *
 * Every request includes the Webyto connection headers via
 * {@link GetglobalsettingsService.getConnectionOptions}. Headers are resolved
 * fresh per call so Setup-screen overrides take effect without a page reload.
 *
 * @remarks
 * All methods apply `retry(1)` on network failure. Errors are surfaced via
 * {@link handleError}, which shows a browser `alert` (legacy pattern flagged
 * for Phase 1 replacement).
 */
@Injectable({
  providedIn: 'root'
})
export class SalesApiService {

  apiURL: string = '';

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  /**
   * Builds an absolute API URL by prepending the runtime API server address.
   *
   * @param url - Path starting with `/`.
   * @returns Absolute URL to the backend endpoint.
   */
  private _getApiUrl(url: string = ''): string {
    let _api: string = this.globalsettingsservice.getApiServerUrl();
    return _api + url;
  }

  // ---------------------------------------------------------------------------
  // Sales leads
  // ---------------------------------------------------------------------------

  /**
   * Returns a paginated, filtered, and sorted list of sales leads.
   *
   * Calls `GET /SalesLeads/List?_fromDate=...&_start=...&_end=...&_sf=...&_so=...&_flt=...`.
   *
   * @param options - Query options with `fd` (from date), `from`/`to`
   *   (pagination), `sp` (sort params), and `fs` (filter string) properties.
   * @returns Observable emitting an array of `SalesLead` records.
   */
  getSalesLeads(options: any): Observable<SalesLead[]> {
    let queryString = '?'
      + (options.fd ? ('&_fromDate=' + encodeURIComponent(options.fd)) : '')
      + (options.from ? ('&_start=' + options.from) : '')
      + (options.to ? ('&_end=' + options.to) : '')
      + ((options.sp && options.sp.sf) ? ('&_sf=' + encodeURIComponent(options.sp.sf)) : '')
      + ((options.sp && options.sp.so) ? ('&_so=' + encodeURIComponent(options.sp.so)) : '')
      + (options.fs ? ('&_flt=' + encodeURIComponent(JSON.stringify(options.fs))) : '')
      ;
    return this.http.get<SalesLead[]>(this._getApiUrl('/SalesLeads/List' + queryString), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a single sales lead record by its primary key.
   *
   * Calls `GET /SalesLeads/:id`.
   *
   * @param id - Sales lead ID (`DOCREGID`).
   * @returns Observable emitting a one-element `SalesLead[]` array.
   */
  getSalesLeadById(id: number): Observable<SalesLead[]> {
    return this.http.get<SalesLead[]>(this._getApiUrl('/SalesLeads/' + id), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Creates a new sales lead record.
   *
   * Calls `POST /SalesLeads/:DOCREGID`.
   *
   * @param rep - The `SalesLead` to create.
   * @returns Observable emitting `{ DOCREGID: number }` with the new ID
   *   (Prompt 13-G — backend returns a single object, not an array; retyped
   *   2026-08-24 to match. Confirmed via the sole call site
   *   (`edit-sales-lead.component.ts`), which already reads `data['DOCREGID']`
   *   — works correctly against the real single-object shape either way, so
   *   this was a stale/misleading type, not a live crash).
   */
  postSalesLeadById(rep: SalesLead): Observable<{ DOCREGID: number }> {
    return this.http.post<{ DOCREGID: number }>(this._getApiUrl('/SalesLeads/' + rep.DOCREGID), rep, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Updates an existing sales lead record.
   *
   * Calls `PUT /SalesLeads/:DOCREGID`.
   *
   * @param rep - The `SalesLead` to update. `DOCREGID` must be set.
   * @returns Observable emitting the number of rows affected.
   */
  putSalesLeadById(rep: SalesLead): Observable<number> {
    return this.http.put<number>(this._getApiUrl('/SalesLeads/' + rep.DOCREGID), rep, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Deletes a sales lead record.
   *
   * Calls `DELETE /SalesLeads/:DOCREGID`.
   *
   * @param rep - The `SalesLead` to delete. `DOCREGID` must be set.
   * @returns Observable emitting the number of rows affected.
   */
  deleteSalesLeadById(rep: SalesLead): Observable<number> {
    return this.http.delete<number>(this._getApiUrl('/SalesLeads/' + rep.DOCREGID), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Sales lead line items
  // ---------------------------------------------------------------------------

  /**
   * Returns all line items for a given sales lead.
   *
   * Calls `GET /SalesLeads/:salesLeadId/Items`.
   *
   * @param salesLeadId - Parent sales lead ID (`DOCREGID`).
   * @returns Observable emitting an array of `SalesLeadItem` records.
   */
  getSalesLeadItems(salesLeadId: number): Observable<SalesLeadItem[]> {
    return this.http.get<SalesLeadItem[]>(this._getApiUrl('/SalesLeads/' + salesLeadId + '/Items'), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a single sales lead line item by lead ID and item ID.
   *
   * Calls `GET /SalesLeads/:salesLeadId/Item/:salesLeadItemId`.
   *
   * @param salesLeadId - Parent sales lead ID (`DOCREGID` / `REGID`).
   * @param salesLeadItemId - Line item ID (`DRID`).
   * @returns Observable emitting a one-element `SalesLeadItem[]` array.
   */
  getSalesLeadItemById(salesLeadId: number, salesLeadItemId: number): Observable<SalesLeadItem[]> {
    return this.http.get<SalesLeadItem[]>(this._getApiUrl('/SalesLeads/' + salesLeadId + '/Item/' + salesLeadItemId), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Creates a new sales lead line item.
   *
   * Calls `POST /SalesLeads/:REGID/Item/:DRID`.
   *
   * @param rep - The `SalesLeadItem` to create. `REGID` and `DRID` must be set.
   * @returns Observable emitting `{ DRID: number }` with the new ID (Prompt
   *   13-G — backend returns this single object, not a plain row-count
   *   number; retyped 2026-08-24 to match. Confirmed via the sole call site
   *   (`edit-sales-lead-items.component.ts`), which only logs the emission —
   *   this was a stale/misleading type, not a live crash).
   */
  postSalesLeadItemById(rep: SalesLeadItem): Observable<{ DRID: number }> {
    return this.http.post<{ DRID: number }>(this._getApiUrl('/SalesLeads/' + rep.REGID + '/Item/' + rep.DRID), rep, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Updates an existing sales lead line item.
   *
   * Calls `PUT /SalesLeads/:REGID/Item/:DRID`.
   *
   * @param rep - The `SalesLeadItem` to update. `REGID` and `DRID` must be set.
   * @returns Observable emitting the number of rows affected.
   */
  putSalesLeadItemById(rep: SalesLeadItem): Observable<number> {
    return this.http.put<number>(this._getApiUrl('/SalesLeads/' + rep.REGID + '/Item/' + rep.DRID), rep, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Deletes a sales lead line item.
   *
   * Calls `DELETE /SalesLeads/:REGID/Item/:DRID`.
   *
   * @param rep - The `SalesLeadItem` to delete. `REGID` and `DRID` must be set.
   * @returns Observable emitting the number of rows affected.
   */
  deleteSalesLeadItemById(rep: SalesLeadItem): Observable<number> {
    return this.http.delete<number>(this._getApiUrl('/SalesLeads/' + rep.REGID + '/Item/' + rep.DRID), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Error handling
  // ---------------------------------------------------------------------------

  /**
   * Centralised HTTP error handler used by all methods in this service.
   *
   * Extracts a human-readable message, shows a browser `alert` (legacy pattern
   * — Phase 1 will replace with a toast), and re-throws so the calling
   * Observable terminates.
   *
   * @param error - Angular `HttpErrorResponse` or `ErrorEvent`.
   * @returns An Observable that immediately errors with the formatted message.
   */
  handleError(error) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    window.alert(errorMessage);
    return throwError(errorMessage);
  }

}
